/**
 * TODO:
 *   1. decouple width change logic to root layout,
 *      so this component is purely responsive to its parent width.
 */

import { useCallback, useEffect, useState } from 'react';

import { Box, IconButton } from '@chakra-ui/react';
import { Dropdown, Menu } from 'antd';
import { useUnit } from 'effector-react';
import { useHistory, useLocation } from 'react-router-dom';
import { useMedia } from 'react-use';

import { isProd } from '@m/config/FeatureFlags';
import { useAdminFeatureFlag } from '@m/hooks/data/featureFlags';
import {
  Analytics,
  CalendarToday,
  Close,
  ContentPasteSearch,
  CorporateFare,
  DirectionsCar,
  Groups,
  LocationOnOutlined,
  Logout,
  Menu as MenuIcon,
  MetropolisGlyph,
  MetropolisLocationGroup,
  MetropolisPoliceOfficer,
  MetropolisWordmark,
  OutlinedFlag,
  Payments,
  Person,
  PersonOutline,
  Rocket,
} from '@m/icons';
import { hasRoles } from '@m/services/keycloak/iamRoles';
import { useSuperAdminCheck } from '@m/services/keycloak/useSuperAdminCheck';
import { fontDefault } from '@m/styled/fonts';
import { RoleName } from '@m/types/api/iam/Iam';
import { authStore, logout } from 'apps/admin/state/auth';

import { LogoArea, LogoWrap, MenuItemContent, Nav, WordmarkWrap } from './Navigation.styled';

import type { MenuProps } from 'antd/lib/menu';

export default function Navigation() {
  const history = useHistory();
  const { isMetropolisUser } = useUnit(authStore);
  const { pathname } = useLocation();
  const surveyRoutes = [
    '/location-survey',
    '/add-zone',
    '/add-lane',
    '/add-display',
    '/prospect-survey',
    '/scouted-location-survey',
  ];
  const hideNavBar = surveyRoutes.some((route) => pathname.includes(route));
  const defaultCollapsed = useMedia('(max-width: 1080px)');
  const smallView = useMedia('(max-width: 420px)');
  const isMobileNav = useMedia('(max-width: 768px)');
  const [desktopCollapsed, setDesktopCollapsed] = useState(defaultCollapsed);
  const [mobileNavOpenPath, setMobileNavOpenPath] = useState<string | null>(null);

  const isSuperAdminPerm = useSuperAdminCheck();
  const { data: paymentCenterEnabled = false } = useAdminFeatureFlag('PaymentCenterView');
  const { data: isProspectingFlowEnabled = false } = useAdminFeatureFlag('ProspectingFlow');
  const { data: managerVenueAndEventsEnabled = false } =
    useAdminFeatureFlag('ManagerVenueAndEvents');
  // if keycloak is not enabled then they are signed in through admin app and are metropolis users
  const hasMetropolisOperator = isMetropolisUser;

  const showSurveysPage =
    hasMetropolisOperator &&
    hasRoles([
      RoleName.SUPER_ADMIN,
      RoleName.LEADER,
      RoleName.OPERATIONAL_ADMIN,
      RoleName.CUSTOMER_SUPPORT,
    ]);
  const topLevelRoute = pathname.split('/')[1];

  const isMobileNavOpen = isMobileNav && mobileNavOpenPath === pathname;

  const closeMobileNav = useCallback(() => {
    setMobileNavOpenPath(null);
  }, []);

  useEffect(() => {
    if (!isMobileNavOpen) return undefined;

    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeMobileNav();
      }
    };

    window.addEventListener('keydown', onEscape);
    return () => {
      window.removeEventListener('keydown', onEscape);
    };
  }, [isMobileNavOpen, closeMobileNav]);

  const collapsed = isMobileNav ? !isMobileNavOpen : desktopCollapsed;
  const navId = 'admin-mobile-navigation';

  const onCollapse = (isCollapsed: boolean, type: string) => {
    if (isMobileNav) return;

    if (type !== 'responsive') {
      setDesktopCollapsed(isCollapsed);
    }
  };

  const superAdminHiddenMenu: MenuProps['items'] = [
    // on click redirect app to notification-center
    hasRoles([RoleName.MANAGER, RoleName.CUSTOMER_SUPPORT])
      ? {
          label: 'Notification Center',
          key: '1',
          onClick: () => {
            history.push('/notification-center');
          },
        }
      : null,
    {
      label: 'Visit Center',
      key: '2',
      onClick: () => {
        history.push('/visit-center');
      },
    },
    hasRoles([RoleName.MANAGER])
      ? {
          label: 'Visit Charge Management',
          key: '3',
          onClick: () => {
            history.push('/visit-charge-management');
          },
        }
      : null,
    !isProd && hasRoles([RoleName.MANAGER])
      ? {
          label: 'Manager Cleanup',
          key: '4',
          onClick: () => {
            history.push('/manager-cleanup');
          },
        }
      : null,
    hasRoles([RoleName.SUPER_ADMIN])
      ? {
          label: 'Scouted Location Tooling',
          key: '5',
          onClick: () => {
            history.push('/prospect-tooling');
          },
        }
      : null,
    hasRoles([RoleName.SUPER_ADMIN])
      ? {
          label: 'Bulk Close Violations',
          key: '6',
          onClick: () => {
            history.push('/bulk-close-violations');
          },
        }
      : null,
    hasRoles([RoleName.SUPER_ADMIN])
      ? {
          label: 'QSR Dev Tools',
          key: '7',
          onClick: () => {
            history.push('/qsr-dev-tools');
          },
        }
      : null,
  ];

  const menuItems: MenuProps['items'] = [
    hasRoles([RoleName.OPERATIONAL_ADMIN, RoleName.CUSTOMER_SUPPORT]) && {
      key: 'operators',
      icon: <PersonOutline />,
      label: (
        <MenuItemContent to="/operators">
          <span>Operators</span>
        </MenuItemContent>
      ),
    },
    hasRoles([RoleName.MANAGER, RoleName.CUSTOMER_SUPPORT]) && {
      key: 'enterprise',
      icon: <CorporateFare />,
      label: (
        <MenuItemContent to="/enterprise">
          <span>Enterprises</span>
        </MenuItemContent>
      ),
    },
    hasRoles([RoleName.MANAGER, RoleName.CUSTOMER_SUPPORT]) && {
      key: 'locations',
      icon: <MetropolisLocationGroup height="1em" width="1em" />,
      label: (
        <MenuItemContent to="/locations">
          <span>Locations</span>
        </MenuItemContent>
      ),
    },
    hasRoles([RoleName.MANAGER, RoleName.CUSTOMER_SUPPORT]) && {
      key: 'zones',
      icon: <LocationOnOutlined />,
      label: (
        <MenuItemContent to="/zones">
          <span>Zones</span>
        </MenuItemContent>
      ),
    },
    hasRoles([RoleName.MANAGER, RoleName.CUSTOMER_SUPPORT]) && {
      key: 'users',
      icon: <Person />,
      label: (
        <MenuItemContent to="/users">
          <span>Users</span>
        </MenuItemContent>
      ),
    },
    showSurveysPage && {
      key: 'surveys',
      icon: <ContentPasteSearch />,
      label: (
        <MenuItemContent to="/surveys">
          <span>Surveys</span>
        </MenuItemContent>
      ),
    },
    hasRoles([
      RoleName.SUPER_ADMIN,
      RoleName.LEADER,
      RoleName.OPERATIONAL_ADMIN,
      RoleName.CUSTOMER_SUPPORT,
      RoleName.MANAGER,
    ]) && {
      key: 'customers',
      icon: <Groups />,
      label: (
        <MenuItemContent to="/customers">
          <span>Customers</span>
        </MenuItemContent>
      ),
    },
    managerVenueAndEventsEnabled &&
      hasRoles([
        RoleName.SUPER_ADMIN,
        RoleName.LEADER,
        RoleName.OPERATIONAL_ADMIN,
        RoleName.CUSTOMER_SUPPORT,
        RoleName.MANAGER,
      ]) && {
        key: 'events',
        icon: <CalendarToday />,
        label: (
          <MenuItemContent to="/events">
            <span>Events</span>
          </MenuItemContent>
        ),
      },
    hasRoles([RoleName.MANAGER, RoleName.CUSTOMER_SUPPORT]) && {
      key: 'enforcement',
      icon: <MetropolisPoliceOfficer height="1em" width="1em" />,
      label: (
        <MenuItemContent to="/enforcement/notices">
          <span>Enforcement</span>
        </MenuItemContent>
      ),
    },
    hasRoles([
      RoleName.SYSTEM_ADMIN,
      RoleName.SUPER_ADMIN,
      RoleName.OPERATIONAL_ADMIN,
      RoleName.LEADER,
      RoleName.MANAGER,
      RoleName.CUSTOMER_SUPPORT,
    ]) && {
      key: 'visit-center',
      icon: <DirectionsCar />,
      label: (
        <MenuItemContent to="/visit-center">
          <span>Visit Center</span>
        </MenuItemContent>
      ),
    },
    paymentCenterEnabled &&
      hasRoles([
        RoleName.SYSTEM_ADMIN,
        RoleName.SUPER_ADMIN,
        RoleName.OPERATIONAL_ADMIN,
        RoleName.LEADER,
        RoleName.MANAGER,
        RoleName.CUSTOMER_SUPPORT,
      ]) && {
        key: 'payment-center',
        icon: <Payments />,
        label: (
          <MenuItemContent to="/payment-center">
            <span>Payment Center</span>
          </MenuItemContent>
        ),
      },
    hasRoles([RoleName.SUPER_ADMIN]) &&
      isSuperAdminPerm && {
        key: 'feature-flags',
        icon: <OutlinedFlag />,
        label: (
          <MenuItemContent to="/feature-flags">
            <span>Feature Flags</span>
          </MenuItemContent>
        ),
      },
    hasRoles([
      RoleName.SYSTEM_ADMIN,
      RoleName.SUPER_ADMIN,
      RoleName.OPERATIONAL_ADMIN,
      RoleName.LEADER,
      RoleName.MANAGER,
    ]) && {
      key: 'client-reporting',
      icon: <Analytics />,
      label: (
        <MenuItemContent to="/client-reporting">
          <span>Reporting</span>
        </MenuItemContent>
      ),
    },
    isProspectingFlowEnabled && {
      key: 'scouted-location-list',
      icon: <Rocket />,
      label: (
        <MenuItemContent to="/scouted-location-list">
          <span>Scouted Locations</span>
        </MenuItemContent>
      ),
    },
    {
      key: 'logout',
      icon: <Logout />,
      label: <span>Log Out</span>,
      onClick: () => {
        logout();
      },
    },
  ].filter((item): item is Exclude<typeof item, boolean> => typeof item !== 'boolean');

  if (hideNavBar) return null;
  return (
    <>
      {isMobileNav && (
        <IconButton
          aria-label={isMobileNavOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-controls={navId}
          aria-expanded={isMobileNavOpen}
          data-testid="NavigationToggle"
          onClick={() => {
            if (isMobileNavOpen) {
              closeMobileNav();
              return;
            }

            setMobileNavOpenPath(pathname);
          }}
          position="fixed"
          top="3"
          right="3"
          zIndex={1000}
          size="sm"
          variant="solid"
          w="10"
          h="10"
          p="2"
          borderRadius="md"
          borderWidth="1px"
          borderColor="whiteAlpha.400"
          boxShadow="sm"
          color={isMobileNavOpen ? 'white' : 'black'}
          bg={isMobileNavOpen ? 'blackAlpha.900' : 'whiteAlpha.900'}
          _hover={{ bg: isMobileNavOpen ? 'gray.800' : 'white' }}
          _active={{ bg: isMobileNavOpen ? 'gray.700' : 'gray.100' }}
        >
          {isMobileNavOpen ? <Close /> : <MenuIcon />}
        </IconButton>
      )}
      {isMobileNav && isMobileNavOpen && (
        <Box
          data-testid="NavigationBackdrop"
          position="fixed"
          inset="0"
          bg="blackAlpha.700"
          zIndex={3}
          onClick={closeMobileNav}
        />
      )}
      <Nav
        id={navId}
        style={{
          // Styles should be applied here instead of in Navigation.styled.ts in order to override
          // the default styles of the antd component.
          userSelect: 'none',
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          insetInlineStart: 0,
          top: 0,
          bottom: 0,
          zIndex: 3,
          scrollbarWidth: 'thin',
          scrollbarGutter: 'stable',
          overflowX: 'hidden',
          fontFamily: fontDefault,
        }}
        data-testid="Navigation"
        collapsible
        breakpoint="md"
        collapsedWidth={isMobileNav || smallView ? 0 : 80}
        $expanded={!collapsed}
        defaultCollapsed={defaultCollapsed}
        onCollapse={onCollapse}
        collapsed={collapsed}
        onBreakpoint={(broken) => {
          if (broken && !isMobileNav) {
            setDesktopCollapsed(true);
          }
        }}
      >
        <LogoArea onClick={(event) => event.stopPropagation()}>
          <LogoWrap>
            <Dropdown
              disabled={
                !isSuperAdminPerm ? !hasRoles([RoleName.CUSTOMER_SUPPORT]) : !isSuperAdminPerm
              }
              menu={{ items: superAdminHiddenMenu }}
              trigger={['contextMenu']}
            >
              <MetropolisGlyph />
            </Dropdown>
          </LogoWrap>
          <WordmarkWrap $expanded={!collapsed}>
            <MetropolisWordmark />
          </WordmarkWrap>
        </LogoArea>
        <Menu
          theme="dark"
          defaultSelectedKeys={['enterprise']}
          selectedKeys={[topLevelRoute, `${topLevelRoute}-list`]}
          mode="inline"
          items={menuItems}
          onClick={closeMobileNav}
        />
      </Nav>
    </>
  );
}
